import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  type BusinessHoursSettings,
  type BusinessHoursWeekday,
  organizationSettingsService,
} from "@/services/organizationSettings.service/organizationSettings.api";
import { Clock, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const WEEKDAYS: { key: BusinessHoursWeekday; label: string }[] = [
  { key: "mon", label: "Segunda" },
  { key: "tue", label: "Terça" },
  { key: "wed", label: "Quarta" },
  { key: "thu", label: "Quinta" },
  { key: "fri", label: "Sexta" },
  { key: "sat", label: "Sábado" },
  { key: "sun", label: "Domingo" },
];

function emptySettings(): BusinessHoursSettings {
  return {
    enabled: false,
    timezone: "America/Sao_Paulo",
    schedule: WEEKDAYS.reduce((acc, d) => {
      acc[d.key] = { enabled: false, periods: [] };
      return acc;
    }, {} as BusinessHoursSettings["schedule"]),
  };
}

export default function BusinessHoursSettings() {
  const [settings, setSettings] = useState<BusinessHoursSettings>(emptySettings());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [data, err] = await organizationSettingsService.getBusinessHours();
      if (cancelled) return;
      if (err || !data) {
        toast.error(err?.msg || "Não foi possível carregar o horário de funcionamento");
        setLoading(false);
        return;
      }
      setSettings(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function updateDayEnabled(day: BusinessHoursWeekday, enabled: boolean) {
    setSettings((prev) => {
      const current = prev.schedule[day] || { enabled: false, periods: [] };
      const periods = enabled && current.periods.length === 0 ? [{ start: "08:00", end: "18:00" }] : current.periods;
      return { ...prev, schedule: { ...prev.schedule, [day]: { enabled, periods } } };
    });
  }

  function updatePeriod(day: BusinessHoursWeekday, index: number, patch: Partial<{ start: string; end: string }>) {
    setSettings((prev) => {
      const periods = [...(prev.schedule[day]?.periods || [])];
      periods[index] = { ...periods[index], ...patch };
      return { ...prev, schedule: { ...prev.schedule, [day]: { ...prev.schedule[day], periods } } };
    });
  }

  function addPeriod(day: BusinessHoursWeekday) {
    setSettings((prev) => {
      const periods = prev.schedule[day]?.periods || [];
      if (periods.length >= 2) return prev;
      return {
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: { ...prev.schedule[day], periods: [...periods, { start: "13:00", end: "18:00" }] },
        },
      };
    });
  }

  function removePeriod(day: BusinessHoursWeekday, index: number) {
    setSettings((prev) => {
      const periods = (prev.schedule[day]?.periods || []).filter((_, i) => i !== index);
      // Sem nenhum turno restante, o dia fica sem sentido continuar "aberto".
      const enabled = periods.length > 0 && prev.schedule[day]?.enabled;
      return { ...prev, schedule: { ...prev.schedule, [day]: { enabled: !!enabled, periods } } };
    });
  }

  async function handleSave() {
    setSaving(true);
    const [data, err] = await organizationSettingsService.updateBusinessHours(settings);
    setSaving(false);
    if (err || !data) {
      toast.error(err?.msg || "Erro ao salvar horário de funcionamento");
      return;
    }
    setSettings(data);
    toast.success("Horário de funcionamento salvo!");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Clock className="h-4 w-4" />
            Horário de funcionamento
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Fora deste horário, quem escrever no WhatsApp recebe uma resposta automática (no
            máximo 1x por dia) e tickets abertos pelo cliente ganham um aviso automático. O texto
            da mensagem é editado em WhatsApp → Templates de plataforma.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, enabled: checked }))}
          />
          <Label className="text-sm">Ativo</Label>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <div className="space-y-1 rounded-lg border border-border p-4">
          {WEEKDAYS.map(({ key, label }) => {
            const day = settings.schedule[key] || { enabled: false, periods: [] };
            return (
              <div
                key={key}
                className="flex flex-wrap items-center gap-3 border-b border-border/60 py-2 last:border-b-0"
              >
                <div className="flex w-32 shrink-0 items-center gap-2">
                  <Switch checked={day.enabled} onCheckedChange={(checked) => updateDayEnabled(key, checked)} />
                  <span className="text-sm">{label}</span>
                </div>

                {day.enabled && day.periods.length > 0 ? (
                  <div className="flex flex-1 flex-wrap items-center gap-2">
                    {day.periods.map((p, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <Input
                          type="time"
                          className="h-8 w-28"
                          value={p.start}
                          onChange={(e) => updatePeriod(key, i, { start: e.target.value })}
                        />
                        <span className="text-xs text-muted-foreground">às</span>
                        <Input
                          type="time"
                          className="h-8 w-28"
                          value={p.end}
                          onChange={(e) => updatePeriod(key, i, { end: e.target.value })}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          aria-label="Remover turno"
                          onClick={() => removePeriod(key, i)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    {day.periods.length < 2 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1"
                        onClick={() => addPeriod(key)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Turno
                      </Button>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Fechado</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end">
        <Button size="sm" className="gap-2" disabled={saving || loading} onClick={handleSave}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Salvar
        </Button>
      </div>
    </div>
  );
}
